import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { AppSettings, Asset, BLOCKING_REASON_TYPES, Edition, Stream } from '@24i/nxg-sdk-photon';
import { ISource, IPlayerEngine, PlayerError, ErrorCodes, IPlayerError } from '@24i/player-base';
import { v4 as uuidv4 } from 'uuid';
// USE /src imports to help next.js process less files to reduce bundle, avoid some problematic files, and speed up build.
import { useContentData } from '@24i/nxg-sdk-smartott-shared/src/context/ContentData';
import { useFeature } from '@24i/nxg-sdk-smartott-shared/src/context/AppSettingsData';
import { allSettled, useEvent } from '@24i/nxg-core-utils/src';
// USE /src imports to help next.js process less files to reduce bundle, avoid some problematic files, and speed up build.

import { usePlayerEngine } from '../../context/PlayerEngine';
import { useSessionId } from '../../analytics/hooks';
import { buildPlayerSource } from './mappers';
import { pickStreamSelector } from './streamScorer';
import { buildAdvertisementParameters } from './advertisementParameters';
import { buildPalSdkNonce } from './buildPalSdkNonce';
import { verifyStreamUrl } from './verifyStream';
import { buildAdjustedStream } from './buildAdjustedStream';

export const ERROR_TYPES_E02 = 'USER_NOT_AUTHENTICATED';
export const ERROR_TYPES_E08 = 'maximum number of concurrent streams reached';
export const ERROR_TYPES_E21 = BLOCKING_REASON_TYPES.PRODUCTS_MISMATCH;

export interface GetStreamOptions {
    isCastConnected?: boolean;
    engine?: IPlayerEngine;
    verifyUrls?: boolean;
}

export interface GetStreamResult {
    edition: Edition;
    stream: Stream;
    source: ISource;
}

export type GetStreamFunc = (asset: Asset, options?: GetStreamOptions) => Promise<GetStreamResult>;

export interface SourceProvider {
    getStream: GetStreamFunc;
}

const transformError = (error: unknown): PlayerError => {
    let code: string = ErrorCodes.PLAYER_FAILURE;
    let message: string | null | undefined;
    let details: Partial<IPlayerError> = {};
    if (typeof error !== 'object' || !error) {
        message = `${error}`;
    } else {
        if ('message' in error) {
            message = error.message as string;
        } else {
            message = 'Something went wrong';
        }
        if (message === ERROR_TYPES_E02) {
            code = 'E02';
        } else if (message === ERROR_TYPES_E08) {
            code = 'E08';
        } else if (message === ERROR_TYPES_E21) {
            code = 'E21';
        } else if ('code' in error) {
            code = error.code as string;
        }
        details = error as IPlayerError;
    }
    return new PlayerError({
        ...details,
        code,
        message,
    });
};

const shouldVerifyUrls = (adInsertionConfig: AppSettings['features']['adInsertion']): boolean => {
    if (!adInsertionConfig?.enabled) return true;
    if (adInsertionConfig.type === 'CSAI') return true;
    return false;
};

export const usePlayerSourceManager = (): SourceProvider => {
    const { prepareStream } = useContentData();
    const palSdkConfig = useFeature('pal_sdk', { enabled: false });
    const adInsertionConfig = useFeature('adInsertion', { enabled: false });
    const privacyConfig = useFeature('data_privacy');
    const { t, i18n } = useTranslation();
    const { engine: defaultEngine } = usePlayerEngine();
    const { sessionId } = useSessionId();
    const playbackId = useRef(uuidv4()).current;

    const getStream: GetStreamFunc = async (
        asset: Asset,
        options?: GetStreamOptions
    ): Promise<GetStreamResult> => {
        if (!asset) {
            throw new PlayerError({
                code: ErrorCodes.PLAYER_FAILURE,
                message: `${t('error.F00.body')} - No Asset`,
            });
        }

        const isCast = options?.isCastConnected ?? false;
        const verifyUrls = options?.verifyUrls ?? shouldVerifyUrls(adInsertionConfig);
        const engine = options?.engine ?? defaultEngine;
        const streamSelector = await pickStreamSelector(isCast, engine);
        const preparedStream = await prepareStream(asset, {
            streamSelector,
            includeAllStreams: verifyUrls,
        });

        const advertisementParameters = adInsertionConfig?.enabled
            ? await buildAdvertisementParameters({
                  privacyConfig,
                  adInsertionConfig,
                  i18n,
                  sessionId,
                  playbackId,
              })
            : undefined;
        const palSdkNonce = palSdkConfig?.enabled
            ? await buildPalSdkNonce(palSdkConfig)
            : undefined;

        const { edition } = preparedStream;
        let { stream } = preparedStream;
        let source: ISource;
        if (preparedStream.allStreams?.length) {
            const adjustedTasks = preparedStream.allStreams.map(async (original) => {
                const score = streamSelector(original.type, original.drm);
                if (score <= 0) return undefined;
                const adjusted = await buildAdjustedStream(
                    adInsertionConfig,
                    original,
                    asset,
                    advertisementParameters,
                    palSdkNonce
                );
                if (!adjusted) return undefined;
                if (verifyUrls && !(await verifyStreamUrl(adjusted))) return undefined;
                return {
                    score,
                    original,
                    adjusted,
                };
            });
            const candidates = await allSettled(adjustedTasks);
            const bestCandidate = candidates
                .flatMap((candidate) => {
                    if (candidate.status === 'rejected') return [];
                    if (!candidate.value) return [];
                    return [candidate.value];
                })
                .sort((a, b) => b.score - a.score)[0];
            if (!bestCandidate) {
                throw new PlayerError({
                    code: ErrorCodes.PLAYER_FAILURE,
                    message: `${t('error.F00.body')} - No valid stream`,
                });
            }
            stream = bestCandidate.original;
            source = buildPlayerSource(bestCandidate.adjusted, edition, asset);
        } else {
            const adjusted = await buildAdjustedStream(
                adInsertionConfig,
                stream,
                asset,
                advertisementParameters,
                palSdkNonce
            );
            source = buildPlayerSource(adjusted, edition, asset);
        }

        return {
            edition,
            stream,
            source,
        };
    };

    // make it safe
    const getStreamExported: GetStreamFunc = useEvent(async (asset, options) => {
        try {
            return await getStream(asset, options);
        } catch (error: unknown) {
            throw transformError(error);
        }
    });
    return useMemo(() => ({ getStream: getStreamExported }), [getStreamExported]);
};
